/**
 * Forward projection of scheduled connector runs (#946).
 *
 * The API's snake_case is kept verbatim rather than mapped to camelCase — this
 * follows useConnectorStats.ts, not the mapping layer in useConnectors.ts.
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';

/** apiClient already prefixes /api/v1 — do not repeat it here. */
const API_BASE = '/connectors';

export type ScheduleExclusionReason =
  | 'no_schedule'
  | 'schedule_paused'
  | 'no_run_in_window'
  | 'invalid_schedule';

export interface ScheduleForecastConnector {
  connector_config_id: number;
  name: string;
  connector_type: string;
  cron_expression: string;
  timezone: string;
  /** null when the connector has no qualifying history — render as unknown, never zero. */
  median_duration_seconds: number | null;
  p90_duration_seconds: number | null;
  sample_count: number;
  run_starts: string[];
  truncated: boolean;
}

export interface ScheduleForecastExcluded {
  connector_config_id: number;
  name: string;
  connector_type: string;
  cron_expression: string | null;
  /** Always present (server-defaulted); unused for `no_schedule` rows, which have no cron_expression. */
  timezone: string;
  median_duration_seconds: number | null;
  reason: ScheduleExclusionReason;
}

export interface ScheduleForecastResponse {
  window_start: string;
  window_end: string;
  schedule_timezone: string;
  lookback_days: number;
  connectors: ScheduleForecastConnector[];
  excluded: ScheduleForecastExcluded[];
}

export interface UseScheduleForecastOptions {
  horizonHours: number;
  lookbackDays?: number;
}

async function fetchScheduleForecast(
  horizonHours: number,
  lookbackDays: number,
): Promise<ScheduleForecastResponse> {
  const response = await apiClient.get(`${API_BASE}/schedule-forecast`, {
    params: { horizon_hours: horizonHours, lookback_days: lookbackDays },
  });
  return response.data;
}

export function useScheduleForecast({
  horizonHours,
  lookbackDays = 30,
}: UseScheduleForecastOptions) {
  return useQuery({
    queryKey: ['scheduleForecast', horizonHours, lookbackDays],
    queryFn: () => fetchScheduleForecast(horizonHours, lookbackDays),
    staleTime: 60_000,
  });
}
